
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedPath } from "./LocalizedRoute";
import { useQuery } from "@tanstack/react-query";
import { getPromoCodes } from "@/services/pricingService";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface NavbarProps {
  rightElement?: React.ReactNode;
}

const Navbar = ({ rightElement }: NavbarProps) => {
  const { user, userRole, signOut } = useAuth();
  const { language, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Promo banner data (integrated into fixed header)
  const { data: promoCodes } = useQuery({
    queryKey: ["promo-codes-nav"],
    queryFn: getPromoCodes,
    staleTime: 5 * 60 * 1000,
  });

  const activePromos = promoCodes?.filter((p) => {
    const now = new Date();
    const start = new Date(p.start_date);
    const expiry = p.expiration_date ? new Date(p.expiration_date) : null;
    return p.public_promo && start <= now && (!expiry || expiry >= now) && (!p.max_uses || p.times_used < p.max_uses);
  }) ?? [];

  const hasBanner = activePromos.length > 0;

  const navItems = [
    { to: "/", key: "home" },
    { to: "/galerie", key: "gallery" },
    { to: "/equipements", key: "equipment" },
    { to: "/activites", key: "activities" },
    { to: "/blog", key: "blog" },
    { to: "/reserver", key: "booking" },
    { to: "/contact", key: "contact" },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  const isActive = (path: string) => {
    const cur = location.pathname === "/" ? "/" : location.pathname.replace(/\/$/, "");
    if (path === "/") return cur === "/";
    return cur.includes(path);
  };

  const onHero = !scrolled;

  return (
    <header className={`fixed w-full z-40 transition-all duration-500 ease-out`}>
      {/* ── Promo announcement strip ── */}
      {hasBanner && (
        <div className="relative overflow-hidden bg-villa-blue text-white">
          <div className="absolute inset-0 bg-gradient-to-r from-villa-turquoise/30 via-transparent to-villa-turquoise/20 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-2 flex items-center justify-center gap-3 flex-wrap text-xs">
            <Sparkles className="h-3 w-3 text-villa-turquoise-light flex-shrink-0" />
            <span className="font-medium text-white/85">Offres en cours :</span>
            <div className="flex gap-2 flex-wrap justify-center">
              {activePromos.map((p) => (
                <Link
                  key={p.id}
                  to={getLocalizedPath("/promotions", language)}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/15 hover:bg-white/25 text-white font-semibold border border-white/20 transition-colors"
                >
                  {p.code} — {p.discount_value}{p.discount_type === "percentage" ? "%" : "€"}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Main nav bar ── */}
      <div className={`transition-all duration-500 ease-out ${scrolled ? "glass h-14" : "bg-transparent py-5"}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-full flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <span className={`font-playfair font-bold tracking-tight text-xl transition-colors duration-300 ${
              onHero ? "text-white" : "text-villa-text"
            }`}>
              Douce<span className={`transition-colors duration-300 ${onHero ? "text-white/80" : "text-villa-turquoise"}`}>Vague</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={getLocalizedPath(item.to, language)}
                className={`relative px-3 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                  onHero
                    ? isActive(item.to) ? "text-white" : "text-white/70 hover:text-white"
                    : isActive(item.to) ? "text-villa-turquoise" : "text-villa-text/65 hover:text-villa-text"
                }`}
              >
                {t(item.key)}
                {isActive(item.to) && (
                  <span className={`absolute bottom-0 left-3 right-3 h-px ${onHero ? "bg-white/50" : "bg-villa-turquoise"}`} />
                )}
              </Link>
            ))}

            {user ? (
              <div className="flex items-center gap-2 ml-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={`px-4 py-2 text-sm font-medium rounded-full border transition-all duration-200 ${
                      onHero
                        ? "border-white/30 text-white hover:bg-white/10"
                        : "border-villa-turquoise/30 text-villa-turquoise hover:bg-villa-turquoise/5"
                    }`}>
                      {t("myAccount")}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-50 bg-white/95 backdrop-blur-xl border-black/[0.06] rounded-2xl shadow-xl p-1 min-w-44">
                    <DropdownMenuItem asChild>
                      <Link to={getLocalizedPath("/profil", language)} className="rounded-xl text-[13px] px-3 py-2">{t("profile")}</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={getLocalizedPath("/mes-reservations", language)} className="rounded-xl text-[13px] px-3 py-2">{t("myReservations")}</Link>
                    </DropdownMenuItem>
                    {(userRole === "admin" || userRole === "gouvernante") && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="rounded-xl text-[13px] px-3 py-2">
                          {userRole === "gouvernante" ? "Gouvernante" : t("admin")}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="rounded-xl text-[13px] px-3 py-2 text-red-500">
                      {t("logOut")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {rightElement}
              </div>
            ) : (
              <div className="flex items-center gap-2 ml-2">
                <Link
                  to="/auth"
                  className={`px-4 py-2 text-sm font-medium rounded-full border transition-all duration-200 ${
                    onHero
                      ? "border-white/30 text-white hover:bg-white/10"
                      : "border-villa-turquoise/30 text-villa-turquoise hover:bg-villa-turquoise/5"
                  }`}
                >
                  {t("logIn")}
                </Link>
                {rightElement}
              </div>
            )}
          </nav>

          {/* Mobile trigger */}
          <div className="flex md:hidden items-center gap-3">
            {rightElement}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  aria-label="Ouvrir le menu"
                  className={`p-2 rounded-full transition-all duration-200 ${
                    onHero ? "text-white hover:bg-white/10" : "text-villa-text hover:bg-black/5"
                  }`}
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </SheetTrigger>
              <SheetContent className="w-full sm:w-96 border-0 bg-white p-0 z-50">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between px-6 py-5 border-b border-black/[0.06]">
                    <span className="font-playfair font-bold text-xl">
                      Douce<span className="text-villa-turquoise">Vague</span>
                    </span>
                  </div>
                  <nav className="flex flex-col px-4 py-6 gap-1 flex-1">
                    {navItems.map((item) => (
                      <Link
                        key={item.key}
                        to={getLocalizedPath(item.to, language)}
                        className={`px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                          isActive(item.to)
                            ? "bg-villa-turquoise/8 text-villa-turquoise"
                            : "text-villa-text/70 hover:text-villa-text hover:bg-black/[0.03]"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t(item.key)}
                      </Link>
                    ))}
                  </nav>
                  <div className="px-4 pb-8 border-t border-black/[0.06] pt-4">
                    {user ? (
                      <div className="flex flex-col gap-1">
                        {[
                          { to: getLocalizedPath("/profil", language), label: t("profile") },
                          { to: getLocalizedPath("/mes-reservations", language), label: t("myReservations") },
                          ...((userRole === "admin" || userRole === "gouvernante") ? [{ to: "/admin", label: userRole === "gouvernante" ? "Gouvernante" : t("admin") }] : []),
                        ].map((link) => (
                          <Link
                            key={link.to}
                            to={link.to}
                            className="px-4 py-3 text-sm text-villa-text/70 hover:text-villa-text rounded-xl hover:bg-black/[0.03] transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {link.label}
                          </Link>
                        ))}
                        <button
                          onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                          className="mt-1 px-4 py-3 text-sm text-left text-red-500 rounded-xl hover:bg-red-50 transition-colors"
                        >
                          {t("logOut")}
                        </button>
                      </div>
                    ) : (
                      <Link
                        to="/auth"
                        className="block w-full text-center px-6 py-3.5 bg-villa-turquoise text-white font-medium text-sm rounded-full hover:bg-villa-turquoise/90 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t("logIn")}
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
